import React, { useEffect, useState } from "react";
import { adminAnalytics } from "../../lib/api";

/**
 * AnalyticsSection — platform-wide stats snapshot from /api/admin/analytics.
 * Plain stat tiles (no charts) so it's read-in-3-seconds for operators.
 *
 * Phase 54 power tool.
 */
export function AnalyticsSection({ onToast }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    setBusy(true);
    try {
      setData(await adminAnalytics());
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't load analytics.");
    } finally { setBusy(false); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  if (!data) {
    return (
      <p
        data-testid="admin-analytics-loading"
        className="text-[12px] text-white/40 text-center py-10"
      >
        {busy ? "Loading platform metrics…" : "—"}
      </p>
    );
  }

  return (
    <div data-testid="admin-analytics-section" className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">
          <i className="fas fa-chart-line mr-1.5" />PLATFORM METRICS
        </p>
        <button
          type="button"
          onClick={reload}
          disabled={busy}
          data-testid="admin-analytics-refresh"
          className="text-[10px] tracking-widest px-3 py-1.5 rounded-lg btn-ghost disabled:opacity-40"
        >
          <i className="fas fa-rotate-right mr-1" />REFRESH
        </button>
      </div>

      <Group title="USERS" testId="admin-analytics-users">
        <Stat label="Total"      value={data.users.total}       testId="stat-users-total" />
        <Stat label="DAU (24h)"  value={data.users.dau_24h}     testId="stat-users-dau" />
        <Stat label="Signups 24h"value={data.users.signups_24h} testId="stat-users-signups-24h" />
        <Stat label="Signups 7d" value={data.users.signups_7d}  testId="stat-users-signups-7d" />
        <Stat label="Signups 30d"value={data.users.signups_30d} testId="stat-users-signups-30d" />
        <Stat label="PRO active" value={data.users.pro_active}  testId="stat-users-pro" accent />
        <Stat label="Suspended"  value={data.users.suspended}   testId="stat-users-suspended" />
      </Group>

      <Group title="LEAGUES" testId="admin-analytics-leagues">
        <Stat label="Total"      value={data.leagues.total}      testId="stat-leagues-total" />
        <Stat label="PRO"        value={data.leagues.pro}        testId="stat-leagues-pro" accent />
        <Stat label="Paused"     value={data.leagues.paused}     testId="stat-leagues-paused" />
        <Stat label="Created 7d" value={data.leagues.created_7d} testId="stat-leagues-created-7d" />
      </Group>

      <Group title="MATCHES" testId="admin-analytics-matches">
        <Stat label="Total challenges" value={data.matches.total_challenges} testId="stat-matches-total" />
        <Stat label="Completed"        value={data.matches.completed}        testId="stat-matches-completed" />
        <Stat label="Completed 7d"     value={data.matches.completed_7d}     testId="stat-matches-completed-7d" />
        <Stat label="Open disputes"    value={data.matches.open_disputes}    testId="stat-matches-disputes" />
      </Group>

      <Group title="TEAMS · REVENUE" testId="admin-analytics-misc">
        <Stat label="Teams total"      value={data.teams.total}                                        testId="stat-teams-total" />
        <Stat label="Tip GMV 30d (USD)" value={`$${(data.revenue?.tip_gmv_usd_30d || 0).toFixed(2)}`}  testId="stat-tip-gmv" accent />
      </Group>

      {Array.isArray(data.top_games) && data.top_games.length > 0 && (
        <div data-testid="admin-analytics-top-games">
          <p className="text-[10px] tracking-[3px] text-white/55 font-bold mb-2">TOP GAMES</p>
          <div className="space-y-1.5">
            {data.top_games.map((g, i) => (
              <div
                key={g.game}
                data-testid={`stat-top-game-${i}`}
                className="flex items-center justify-between glass rounded-xl px-3 py-2 border border-white/8"
              >
                <span className="text-sm text-white truncate">
                  <span className="text-[10px] text-[#14b8a6] mr-2">#{i + 1}</span>
                  {g.game}
                </span>
                <span className="text-sm font-mono text-[#14b8a6]">{g.players}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="text-[9px] tracking-widest text-white/30 text-right">
        Generated {new Date(data.generated_at).toLocaleString()}
      </p>
    </div>
  );
}

function Group({ title, testId, children }) {
  return (
    <div data-testid={testId}>
      <p className="text-[10px] tracking-[3px] text-white/55 font-bold mb-2">{title}</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
        {children}
      </div>
    </div>
  );
}

function Stat({ label, value, accent, testId }) {
  return (
    <div
      data-testid={testId}
      className={`glass rounded-xl px-3 py-2.5 border ${
        accent ? "border-[#14b8a6]/45" : "border-white/8"
      }`}
    >
      <p className="text-[9px] tracking-widest text-white/45">{label}</p>
      <p className={`text-xl font-mono mt-0.5 ${accent ? "text-[#14b8a6]" : "text-white"}`}>
        {value}
      </p>
    </div>
  );
}

export default AnalyticsSection;
